import { useMemo } from 'react';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ApprovalConflictNotice } from '../VacationApprovalCard/components/ApprovalConflictNotice';
import { VacationEventHistory } from '../VacationEventHistory';
import { SummaryPeriods, SummaryFacts, SummaryApproval } from '../VacationRequestSummary/components';
import { TeamOverlapNotice, SegmentDecisionControl, DpChoiceGroup } from './components';
import { findRequestTeamOverlaps } from '../../utils/findRequestTeamOverlaps';
import { resolveManagerEmail } from '../../utils/vacationRequestStatus';
import { createDpDecisionSchema } from '../../models/vacation.schemas';
import type { DpDecisionFormData } from '../../models/vacation.schemas';
import type { VacationRequest } from '../../models/vacation.types';
import type { VacationDpRow } from '../../models/vacationDpBoard.types';
import type { VacationEvent } from '../../models/vacationEvent.types';
import {
    getRequestStatusMetadata,
    getReceiptPendingLabel,
    getCollaboratorProfileHref,
    getProfileLinkLabel,
    getModalityLabel,
} from '../../vacations.definitions';
import { formatDateSafe } from '@/utils/dateUtils';
import {
    CANCEL_LABEL,
    CLOSE_LABEL,
    CONFIRM_LABEL,
    MODAL_TITLE,
    NOT_DECIDABLE_HINT,
    OWN_REQUEST_WARNING,
    SELL_APPROVE_LABEL,
    SELL_HINT,
    SELL_LEGEND,
    SELL_REJECT_LABEL,
    SUBMITTING_LABEL,
} from './vacationDpDecisionModal.definitions';
import type { DpDecisionSubmit } from './vacationDpDecisionModal.types';

export interface VacationDpDecisionPanelProps {
    row: VacationDpRow;
    request: VacationRequest;
    allRows: VacationDpRow[];
    titleId: string;
    isSubmitting: boolean;
    isOwnRequest: boolean;
    onClose: () => void;
    onConfirm: (row: VacationDpRow, decision: DpDecisionSubmit) => void;
    historyEvents?: VacationEvent[];
}

export function VacationDpDecisionPanel({
    row,
    request,
    allRows,
    titleId,
    isSubmitting,
    isOwnRequest,
    onClose,
    onConfirm,
    historyEvents,
}: VacationDpDecisionPanelProps) {
    const hasSellRequest = Boolean(request.sellRequest.requested);
    const isDecidable = request.status === 'PendingDP';
    const segmentCount = request.segments?.length ?? 1;

    const pendingOrders = useMemo(
        () =>
            (request.segments ?? [])
                .filter((seg) => request.dpSegmentDecisions?.[seg.order]?.status !== 'Approved')
                .map((seg) => String(seg.order)),
        [request.segments, request.dpSegmentDecisions]
    );

    const segmentDecisionDefaults = useMemo(() => {
        const defaults: Record<string, { decision: undefined; note: '' }> = {};
        pendingOrders.forEach((order) => {
            defaults[order] = { decision: undefined, note: '' };
        });
        return defaults;
    }, [pendingOrders]);

    const form = useForm<DpDecisionFormData>({
        resolver: zodResolver(createDpDecisionSchema({ hasSellRequest, segmentCount, pendingOrders })),
        defaultValues: { segmentDecisions: segmentDecisionDefaults },
    });

    const { control, handleSubmit, watch, formState: { errors } } = form;
    const segmentDecisions = watch('segmentDecisions');

    const statusMeta = getRequestStatusMetadata(request);
    const receiptPendingLabel = getReceiptPendingLabel(request);

    const allDecided = pendingOrders.every((order) => Boolean(segmentDecisions?.[order]?.decision));
    const allPendingRejected = pendingOrders.every((order) => segmentDecisions?.[order]?.decision === 'reject');

    const submit = handleSubmit((data) => {
        const converted: any = {
            segmentDecisions: data.segmentDecisions,
            sellDecision: data.sellDecision,
        };
        onConfirm(row, converted);
    });

    return (
        <FormProvider {...form}>
            <form onSubmit={submit} className="flex min-h-0 flex-col">
                <div className="border-b border-gray-200 px-7 py-5">
                    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                        <div className="min-w-0">
                            <h2 id={titleId} className="text-lg font-semibold text-raro-gray-900 font-montserrat">
                                {MODAL_TITLE}
                            </h2>
                            <p className="mt-1 text-base text-raro-gray-700 font-montserrat">
                                {row.collaboratorName}
                                {row.collaboratorRole ? ` · ${row.collaboratorRole}` : ''}
                            </p>
                        </div>

                        <div className="flex shrink-0 flex-wrap items-center gap-3">
                            <span className={clsx('rounded-md px-2.5 py-1 text-sm font-montserrat', statusMeta.className)}>
                                {statusMeta.label}
                            </span>
                            {receiptPendingLabel && (
                                <span className="rounded-md bg-amber-100 px-2.5 py-1 text-sm text-amber-800 font-montserrat">
                                    {receiptPendingLabel}
                                </span>
                            )}
                            <Link
                                to={getCollaboratorProfileHref(row.collaboratorId)}
                                className="text-sm text-primary-700 underline-offset-2 hover:underline font-montserrat"
                            >
                                {getProfileLinkLabel(row.collaboratorName)}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-7 py-5">
                    <div className="rounded-2xl border border-raro-gray-100 p-4 space-y-4">
                        <SummaryPeriods
                            segments={request.segments}
                            dpSegmentDecisions={request.dpSegmentDecisions}
                            requestStatus={request.status}
                            renderAction={
                                isDecidable
                                    ? (segment, previousDecision) => (
                                          <SegmentDecisionControl
                                              key={segment.order}
                                              segment={segment}
                                              previousDecision={previousDecision}
                                          />
                                      )
                                    : undefined
                            }
                        />

                        <SummaryFacts
                            modalityLabel={getModalityLabel(request.modality)}
                            sellRequest={request.sellRequest}
                            deadline={formatDateSafe(row.concessiveDeadline || request.concessiveDeadline)}
                            deadlineRaw={row.concessiveDeadline || request.concessiveDeadline}
                            showProjectedHint={row.deadlineIsProjected && !request.concessiveDeadline}
                        />

                        <SummaryApproval
                            managerEmail={resolveManagerEmail(request) || ''}
                            managerName={row.managerName}
                            requestedAt={request.requestedAt ? formatDateSafe(request.requestedAt.toDate?.() || request.requestedAt) : null}
                        />
                    </div>

                    <ApprovalConflictNotice conflicts={request.conflictsSnapshot} segments={request.segments} />

                    <TeamOverlapNotice overlaps={findRequestTeamOverlaps(request, allRows)} />

                    <VacationEventHistory
                        request={request}
                        requestId={historyEvents ? undefined : request.id}
                        events={historyEvents}
                    />

                    {!isDecidable && (
                        <div className="flex gap-2.5 rounded-xl bg-raro-surface-50 p-4">
                            <InformationCircleIcon className="h-5 w-5 shrink-0 text-primary-500" aria-hidden="true" />
                            <p className="text-sm text-raro-gray-700 font-montserrat">{NOT_DECIDABLE_HINT}</p>
                        </div>
                    )}

                    {isOwnRequest && (
                        <div className="flex gap-2.5 rounded-xl bg-amber-50 p-4">
                            <InformationCircleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                            <p className="text-sm text-amber-900 font-montserrat">{OWN_REQUEST_WARNING}</p>
                        </div>
                    )}

                    {isDecidable && hasSellRequest && Object.values(segmentDecisions || {}).some((sd) => sd?.decision === 'approve') && (
                        <Controller
                            control={control}
                            name="sellDecision"
                            render={({ field }) => (
                                <DpChoiceGroup
                                    name="dp-sell-decision"
                                    legend={SELL_LEGEND}
                                    hint={SELL_HINT}
                                    choices={[
                                        { value: 'approve', label: SELL_APPROVE_LABEL },
                                        { value: 'reject', label: SELL_REJECT_LABEL },
                                    ]}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.sellDecision?.message}
                                />
                            )}
                        />
                    )}
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-7 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-raro-gray-700 transition-colors hover:bg-gray-50 font-montserrat"
                    >
                        {isDecidable ? CANCEL_LABEL : CLOSE_LABEL}
                    </button>

                    {isDecidable && (
                        <button
                            type="submit"
                            disabled={isSubmitting || !allDecided || isOwnRequest}
                            className={clsx(
                                'rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60 font-montserrat',
                                allPendingRejected ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-500 hover:bg-primary-600'
                            )}
                        >
                            {isSubmitting ? SUBMITTING_LABEL : CONFIRM_LABEL}
                        </button>
                    )}
                </div>
            </form>
        </FormProvider>
    );
}

export default VacationDpDecisionPanel;
